import { cn } from '@plato/ui/util/classes';
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';

export type PushStackLevel = {
    key: string;
    content: ReactNode;
};

export type PushStackProps = {
    /** Index into `levels` of the level currently on screen. */
    activeIndex: number;
    levels: PushStackLevel[];
};

/**
 * Push-down navigation inside a single surface: the active level sits in flow and
 * drives the height, the others wait as faded overlays at the same origin, and the
 * container morphs between them. Deeper levels wait to the right and shallower ones
 * to the left, so a drill-in slides forward and a back slides back.
 */
export function PushStack({ activeIndex, levels }: PushStackProps) {
    const activeRef = useRef<HTMLDivElement>(null);
    const [height, setHeight] = useState<number | undefined>(undefined);

    useLayoutEffect(() => {
        const element = activeRef.current;
        if (!element) {
            return;
        }
        const measure = (): void => {
            setHeight(element.offsetHeight);
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(element);
        return () => {
            observer.disconnect();
        };
    }, [activeIndex]);

    return (
        <div
            className="relative overflow-hidden transition-[height] duration-state ease-decelerate"
            style={height === undefined ? undefined : { height }}
        >
            {levels.map((level, index) => {
                const isActive = index === activeIndex;
                return (
                    <div
                        key={level.key}
                        ref={isActive ? activeRef : undefined}
                        inert={!isActive}
                        className={cn(
                            isActive ? 'relative' : 'absolute inset-x-0 top-0',
                            'transition-[translate,opacity] duration-state ease-decelerate',
                            isActive
                                ? 'translate-x-0 opacity-100'
                                : cn(
                                      'pointer-events-none opacity-0',
                                      index > activeIndex ? 'translate-x-2' : '-translate-x-2',
                                  ),
                        )}
                    >
                        {level.content}
                    </div>
                );
            })}
        </div>
    );
}
